import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './FreeGiftPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
// Same reasoning as the Discounts sync button: this can scan the full product catalog, which may
// run past Firebase Hosting's ~60s rewrite timeout, so it calls the function's direct Cloud Run
// URL instead (honors the function's own longer timeout).
const FREE_GIFT_SYNC_DIRECT_URL = 'https://app-frgo5nxgza-uc.a.run.app/api/v1/admin/free-gift/sync';

const CRON_FREQUENCY_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'every_3_days', label: 'Every 3 days' },
  { value: 'weekly', label: 'Weekly' },
];

// Same 60s-Hosting-timeout reasoning as the metafield sync button above.
const FREE_GIFT_TIERS_SYNC_DIRECT_URL = 'https://app-frgo5nxgza-uc.a.run.app/api/v1/admin/free-gift/tiers/sync';

const TRACK_OPTIONS = [
  { value: '', label: 'Unassigned' },
  { value: 'retail_track1', label: 'Retail Track 1 (digital-only)' },
  { value: 'retail_track2', label: 'Retail Track 2 (all products)' },
  { value: 'wholesale_track1', label: 'Wholesale Track 1 (digital-only)' },
  { value: 'wholesale_track2', label: 'Wholesale Track 2 (all products)' },
];

function formatDateTime(value) {
  if (!value) return '—';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleString();
}

export default function FreeGiftPage() {
  const { currentUser } = useAuth();

  const [settings, setSettings] = useState({
    productsPerCategory: 50,
    cronFrequency: 'every_3_days',
    excludedProductIds: [],
    polyToPureThreshold: 0,
    lastRunAt: null,
    lastRunCounts: null,
  });
  const [excludedText, setExcludedText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);

  const [tiers, setTiers] = useState([]);
  const [tiersLoading, setTiersLoading] = useState(true);
  const [tiersSyncing, setTiersSyncing] = useState(false);
  const [tiersMessage, setTiersMessage] = useState(null);

  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    const resolvedUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authedFetch('/api/v1/admin/free-gift/settings');
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load settings.');
      setSettings(data.settings);
      setExcludedText((data.settings.excludedProductIds || []).join('\n'));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = useCallback(async () => {
    setSaving(true);
    setMessage(null);
    try {
      const excludedProductIds = excludedText
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const res = await authedFetch('/api/v1/admin/free-gift/settings', {
        method: 'POST',
        body: JSON.stringify({
          productsPerCategory: settings.productsPerCategory,
          cronFrequency: settings.cronFrequency,
          excludedProductIds,
          polyToPureThreshold: settings.polyToPureThreshold,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed.');
      setSettings(data.settings);
      setExcludedText((data.settings.excludedProductIds || []).join('\n'));
      setMessage({ type: 'success', text: 'Settings saved.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSaving(false);
    }
  }, [authedFetch, settings.productsPerCategory, settings.cronFrequency, settings.polyToPureThreshold, excludedText]);

  const runNow = useCallback(async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const res = await authedFetch(FREE_GIFT_SYNC_DIRECT_URL, { method: 'POST' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Sync failed.');
      setMessage({
        type: 'success',
        text: `Synced — ${data.pure_count} pure, ${data.poly_count} poly, ${data.plain_count} plain, ${data.cleared} cleared.`,
      });
      await loadSettings();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSyncing(false);
    }
  }, [authedFetch, loadSettings]);

  const loadTiers = useCallback(async () => {
    setTiersLoading(true);
    try {
      const res = await authedFetch('/api/v1/admin/free-gift/tiers');
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load tiers.');
      setTiers(data.tiers || []);
    } catch (err) {
      setTiersMessage({ type: 'error', text: err.message });
    } finally {
      setTiersLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadTiers();
  }, [loadTiers]);

  const syncTiers = useCallback(async () => {
    setTiersSyncing(true);
    setTiersMessage(null);
    try {
      const res = await authedFetch(FREE_GIFT_TIERS_SYNC_DIRECT_URL, { method: 'POST' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Tier sync failed.');
      setTiersMessage({
        type: 'success',
        text: `Synced ${data.fetched} Bxgy tiers — ${data.created} new, ${data.updated} updated, ${data.removed} removed.`,
      });
      await loadTiers();
    } catch (err) {
      setTiersMessage({ type: 'error', text: err.message });
    } finally {
      setTiersSyncing(false);
    }
  }, [authedFetch, loadTiers]);

  const updateTierTrack = useCallback(async (code, track) => {
    try {
      const res = await authedFetch(`/api/v1/admin/free-gift/tiers/${encodeURIComponent(code)}/track`, {
        method: 'PATCH',
        body: JSON.stringify({ track: track || null }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to update track.');
      setTiers((prev) => prev.map((t) => (t.code === code ? { ...t, track: track || null } : t)));
    } catch (err) {
      setTiersMessage({ type: 'error', text: err.message });
    }
  }, [authedFetch]);

  if (loading) {
    return <div className="free-gift-page"><div className="free-gift-loading">Loading settings...</div></div>;
  }

  return (
    <div className="free-gift-page">
      <div className="free-gift-header">
        <div>
          <h1>Free Gift Collection</h1>
          <p>Marks top in-stock products per fabric category with a Shopify metafield so automated collections auto-populate.</p>
        </div>
        <div className="free-gift-header-actions">
          <button className="free-gift-btn free-gift-btn-primary" onClick={runNow} disabled={syncing}>
            {syncing ? 'Running…' : 'Run Now'}
          </button>
          <span className="free-gift-last-run">Last run: <strong>{formatDateTime(settings.lastRunAt)}</strong></span>
        </div>
      </div>

      {message ? <div className={`free-gift-alert free-gift-alert-${message.type}`}>{message.text}</div> : null}

      {settings.lastRunCounts ? (
        <div className="free-gift-summary">
          <div><span>Pure</span><strong>{settings.lastRunCounts.pure_count}</strong></div>
          <div><span>Poly</span><strong>{settings.lastRunCounts.poly_count}</strong></div>
          <div><span>Plain</span><strong>{settings.lastRunCounts.plain_count}</strong></div>
          <div><span>Cleared</span><strong>{settings.lastRunCounts.cleared}</strong></div>
        </div>
      ) : null}

      <div className="free-gift-section">
        <label className="free-gift-field">
          Products per category
          <input
            type="number"
            min={1}
            value={settings.productsPerCategory}
            onChange={(e) => setSettings((s) => ({ ...s, productsPerCategory: parseInt(e.target.value, 10) || 1 }))}
          />
          <span className="free-gift-field-hint">How many top in-stock products per category (pure/poly/plain) get the metafield.</span>
        </label>

        <label className="free-gift-field">
          Poly → Pure price threshold (₹)
          <input
            type="number"
            min={0}
            value={settings.polyToPureThreshold}
            onChange={(e) => setSettings((s) => ({ ...s, polyToPureThreshold: parseFloat(e.target.value) || 0 }))}
          />
          <span className="free-gift-field-hint">
            A poly-categorized product whose fabric spec sell price is above this moves into the pure category instead. Set to 0 to disable.
          </span>
        </label>

        <label className="free-gift-field">
          Cron frequency
          <select
            value={settings.cronFrequency}
            onChange={(e) => setSettings((s) => ({ ...s, cronFrequency: e.target.value }))}
          >
            {CRON_FREQUENCY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <span className="free-gift-field-hint">
            Not yet wired to the actual schedule — the background sync currently runs on a fixed 3-day cadence regardless of this setting.
          </span>
        </label>

        <label className="free-gift-field">
          Excluded product IDs
          <textarea
            rows={4}
            placeholder="One product ID per line, or comma-separated"
            value={excludedText}
            onChange={(e) => setExcludedText(e.target.value)}
          />
          <span className="free-gift-field-hint">These products are always skipped, regardless of stock or category.</span>
        </label>

        <div className="free-gift-btn-row">
          <button className="free-gift-btn free-gift-btn-secondary" onClick={saveSettings} disabled={saving}>
            {saving ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="free-gift-header" style={{ marginTop: 32 }}>
        <div>
          <h2>Free Gift Tiers (Buy X Get Y)</h2>
          <p>Syncs meter-based tiers directly from your real Shopify Bxgy discount codes. Assign each one to a track so the cart-eligibility API knows how to group it.</p>
        </div>
        <div className="free-gift-header-actions">
          <button className="free-gift-btn free-gift-btn-primary" onClick={syncTiers} disabled={tiersSyncing}>
            {tiersSyncing ? 'Syncing…' : 'Sync Tiers from Shopify'}
          </button>
        </div>
      </div>

      {tiersMessage ? <div className={`free-gift-alert free-gift-alert-${tiersMessage.type}`}>{tiersMessage.text}</div> : null}

      {tiersLoading ? (
        <div className="free-gift-loading">Loading tiers...</div>
      ) : (
        <div className="free-gift-section" style={{ maxWidth: 'none' }}>
          <table className="free-gift-tiers-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Min Meters</th>
                <th>Free Meters</th>
                <th>Collections</th>
                <th>Status</th>
                <th>Track</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((tier) => (
                <tr key={tier.code}>
                  <td><strong>{tier.code}</strong></td>
                  <td>{tier.minMeters}</td>
                  <td>{tier.freeMeters}</td>
                  <td>{(tier.collectionLabels || []).join(', ')}</td>
                  <td>{tier.status}</td>
                  <td>
                    <select
                      value={tier.track || ''}
                      onChange={(e) => updateTierTrack(tier.code, e.target.value)}
                    >
                      {TRACK_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
              {!tiers.length ? (
                <tr><td colSpan="6" className="discounts-empty-cell">No tiers synced yet — click "Sync Tiers from Shopify".</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
